/**
 * Language Helper Utilities for Multi-Language Forms Feature
 *
 * Provides functions for:
 * - Getting localized field values from controls (using fieldEs, fieldRu, etc. suffix convention)
 * - Managing language preferences (localStorage, URL params)
 * - Language detection
 */

const LANGUAGE_STORAGE_KEY = 'teamio_language_pref';
const DEFAULT_LANGUAGE = 'en';

/**
 * All languages the system knows about.
 * To add a new language: add an entry here, add systemTranslations in systemTranslations.js,
 * and ensure the backend has AWS Translate support for the language code.
 */
const LANGUAGE_CONFIG = {
  en: { name: 'English', nativeName: 'English', flag: '\u{1F1FA}\u{1F1F8}', browserPrefixes: ['en'] },
  es: { name: 'Spanish', nativeName: 'Espa\u00f1ol', flag: '\u{1F1EA}\u{1F1F8}', browserPrefixes: ['es'] },
  ru: { name: 'Russian', nativeName: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439', flag: '\u{1F1F7}\u{1F1FA}', browserPrefixes: ['ru'] }
};

// All known language codes (for validation)
const SUPPORTED_LANGUAGES = Object.keys(LANGUAGE_CONFIG);

/**
 * Compute the field suffix for a language code.
 * 'es' → 'Es', 'ru' → 'Ru'
 * Must match the backend's suffix computation in mergeTranslations().
 */
function langSuffix(lang) {
  if (!lang || lang === 'en') return '';
  return lang.charAt(0).toUpperCase() + lang.slice(1);
}

/**
 * Get localized field value from a control or config object.
 * Looks up the suffixed field (e.g., labelEs, labelRu) and falls back to English.
 *
 * @param {Object} obj - Control or config object
 * @param {string} field - Field name (e.g., 'label', 'placeholder')
 * @param {string} language - Target language code
 * @returns {string} - Localized value or fallback to English
 *
 * @example
 * // control = { label: 'First Name', labelEs: 'Nombre', labelRu: 'Имя' }
 * getLocalizedField(control, 'label', 'es') // Returns 'Nombre'
 * getLocalizedField(control, 'label', 'ru') // Returns 'Имя'
 * getLocalizedField(control, 'label', 'en') // Returns 'First Name'
 */
export function getLocalizedField(obj, field, language = 'en') {
  if (!obj || !field) return '';

  if (language !== 'en') {
    const suffix = langSuffix(language);
    const localizedField = `${field}${suffix}`;
    // Return localized if available, otherwise fall back to English
    return obj[localizedField] !== undefined && obj[localizedField] !== null && obj[localizedField] !== ''
      ? obj[localizedField]
      : obj[field] || '';
  }

  return obj[field] || '';
}

/**
 * Get localized item from an array (for dropdowns, radio buttons, etc.)
 * Handles both string arrays and object arrays with label property
 *
 * @param {Object} control - Control object containing items/options
 * @param {number} index - Index of the item
 * @param {string} language - Target language
 * @param {string} arrayField - 'items' or 'options'
 * @returns {string} - Localized item label
 */
export function getLocalizedArrayItem(control, index, language = 'en', arrayField = 'items') {
  if (!control || !control[arrayField]) return '';

  const items = control[arrayField];
  const item = items[index];

  if (item === undefined) return '';

  // Handle string arrays
  if (typeof item === 'string') {
    if (language !== 'en') {
      const suffix = langSuffix(language);
      const localizedArray = control[`${arrayField}${suffix}`];
      if (localizedArray && localizedArray[index] !== undefined && localizedArray[index] !== '') {
        return localizedArray[index];
      }
    }
    return item;
  }

  // Handle object arrays with label property
  if (item && typeof item === 'object') {
    return getLocalizedField(item, 'label', language);
  }

  return '';
}

/**
 * Get all localized items from an array
 *
 * @param {Object} control - Control object
 * @param {string} language - Target language
 * @param {string} arrayField - 'items' or 'options'
 * @returns {Array} - Array of localized labels
 */
export function getLocalizedArrayItems(control, language = 'en', arrayField = 'items') {
  if (!control || !control[arrayField]) return [];

  return control[arrayField].map((_, index) =>
    getLocalizedArrayItem(control, index, language, arrayField)
  );
}

/**
 * Get language preference with precedence:
 * 1. URL parameter (?lang=es)
 * 2. localStorage
 * 3. Browser language detection
 * 4. Default to English
 *
 * @returns {string} - Language code
 */
export function getLanguagePreference() {
  // 1. Check URL parameter (highest priority)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang)) {
      return urlLang;
    }
  }

  // 2. Check localStorage
  if (typeof localStorage !== 'undefined') {
    const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang)) {
      return storedLang;
    }
  }

  // 3. Check browser language against all configured languages
  if (typeof navigator !== 'undefined') {
    const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase();
    if (browserLang) {
      for (const [langCode, config] of Object.entries(LANGUAGE_CONFIG)) {
        if (langCode === 'en') continue; // Don't match English — it's the default fallback
        if (config.browserPrefixes.some(prefix => browserLang.startsWith(prefix))) {
          return langCode;
        }
      }
    }
  }

  // 4. Default to English
  return DEFAULT_LANGUAGE;
}

/**
 * Save language preference to localStorage
 *
 * @param {string} language - Language code to save
 */
export function setLanguagePreference(language) {
  if (SUPPORTED_LANGUAGES.includes(language) && typeof localStorage !== 'undefined') {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }
}

/**
 * Check if a form supports a specific language (or any non-English language)
 *
 * @param {Object} formConfig - Form configuration object (the full ConfigData)
 * @param {string} [language] - Specific language to check. If omitted, checks for any non-English.
 * @returns {boolean}
 */
export function formSupportsLanguage(formConfig, language) {
  if (!formConfig) return false;

  const supportedLangs = formConfig.formConfig?.supportedLanguages;

  if (supportedLangs && Array.isArray(supportedLangs)) {
    if (language) {
      return supportedLangs.includes(language);
    }
    // Check for any non-English language
    return supportedLangs.some(l => l !== 'en');
  }

  // Fallback: Check if any control has translated fields (for backward compat)
  if (formConfig.controls) {
    for (const controlKey in formConfig.controls) {
      const control = formConfig.controls[controlKey];
      // Check for any known language suffix
      for (const langCode of SUPPORTED_LANGUAGES) {
        if (langCode === 'en') continue;
        const suffix = langSuffix(langCode);
        if (control[`label${suffix}`] || control[`placeholder${suffix}`]) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * @deprecated Use formSupportsLanguage(formConfig) or formSupportsLanguage(formConfig, 'es') instead
 */
export function formSupportsSpanish(formConfig) {
  return formSupportsLanguage(formConfig, 'es');
}

/**
 * Get the list of non-English languages a form supports
 *
 * @param {Object} formConfig - Form configuration object (the full ConfigData)
 * @returns {string[]} - Array of language codes (e.g., ['es', 'ru'])
 */
export function getFormLanguages(formConfig) {
  if (!formConfig) return [];
  const supportedLangs = formConfig.formConfig?.supportedLanguages;
  if (supportedLangs && Array.isArray(supportedLangs)) {
    return supportedLangs.filter(l => l !== 'en');
  }
  return [];
}

/**
 * Get language display name
 *
 * @param {string} langCode - Language code
 * @returns {string} - Display name
 */
export function getLanguageDisplayName(langCode) {
  return LANGUAGE_CONFIG[langCode]?.nativeName || LANGUAGE_CONFIG[langCode]?.name || langCode;
}

/**
 * Get language flag emoji
 *
 * @param {string} langCode - Language code
 * @returns {string} - Flag emoji
 */
export function getLanguageFlag(langCode) {
  return LANGUAGE_CONFIG[langCode]?.flag || '';
}

/**
 * Get the available languages for a form (including English), as objects for UI display
 *
 * @param {Object} formConfig - Form configuration object (the full ConfigData)
 * @returns {Array<{code: string, name: string, flag: string}>}
 */
export function getAvailableLanguagesForForm(formConfig) {
  const languages = [{ code: 'en', name: LANGUAGE_CONFIG.en.nativeName, flag: LANGUAGE_CONFIG.en.flag }];
  const formLangs = getFormLanguages(formConfig);
  for (const lang of formLangs) {
    if (LANGUAGE_CONFIG[lang]) {
      languages.push({ code: lang, name: LANGUAGE_CONFIG[lang].nativeName, flag: LANGUAGE_CONFIG[lang].flag });
    }
  }
  return languages;
}

export { langSuffix, LANGUAGE_CONFIG, SUPPORTED_LANGUAGES };

export default {
  getLocalizedField,
  getLocalizedArrayItem,
  getLocalizedArrayItems,
  getLanguagePreference,
  setLanguagePreference,
  formSupportsLanguage,
  formSupportsSpanish,
  getFormLanguages,
  getLanguageDisplayName,
  getLanguageFlag,
  getAvailableLanguagesForForm,
  langSuffix,
  LANGUAGE_CONFIG,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE
};
